import React, { memo, useCallback, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useGetOnDemandStaffByManagementCompanyQuery } from '../../../redux/managementCompanyWorkforce';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import personIcon from '../../../assets/images/person-icon.svg';

import '../../../components/professionals/ProfessionalsList.scss';
import './ShiftMedOnDemandStaffList.scss';

// Column widths are shared between the main table and the expansion sub-table (both use a fixed table
// layout) so the sub-table's "License type"/"Specialty" columns line up under the matching headers.
const COLUMN_WIDTHS = {
	expander: '4%',
	professional: '22%',
	phone: '16%',
	licenseType: '18%',
	specialty: '32%',
	actions: '8%'
};

// Leading spacer in the sub-table spans the expander + Professional + Phone columns.
const EXPANSION_LEADING_WIDTH = '42%';

// Sorting is not implemented for v1 — columns are marked sortable only so the header shows the standard
// sort caret, and this no-op keeps the table controlled so clicking never reorders the rows.
const noopSort = () => {};

// License type: the professional's distinct licenses (short codes), in the order the backend returns them.
const formatLicenseType = (shiftTypes) => {
	const licenses = [];
	shiftTypes.forEach((shiftType) => {
		if (!licenses.includes(shiftType.licenseShortCode)) {
			licenses.push(shiftType.licenseShortCode);
		}
	});
	return licenses.join(', ');
};

// Specialty: distinct specialties grouped under their parent license (e.g. "RN - Med/Surg, Tele").
// Shift types that are a bare license (no specialty) contribute only to the License type column.
const formatSpecialty = (shiftTypes) => {
	const specialtiesByLicense = new Map();
	shiftTypes.forEach((shiftType) => {
		if (!shiftType.specialtyShortName) return;
		if (!specialtiesByLicense.has(shiftType.licenseShortCode)) {
			specialtiesByLicense.set(shiftType.licenseShortCode, []);
		}
		const specialties = specialtiesByLicense.get(shiftType.licenseShortCode);
		if (!specialties.includes(shiftType.specialtyShortName)) {
			specialties.push(shiftType.specialtyShortName);
		}
	});
	const groups = [];
	specialtiesByLicense.forEach((specialties, license) => {
		groups.push(`${license} - ${specialties.join(', ')}`);
	});
	return groups.length ? groups.join('; ') : '-';
};

const mapOnDemandStaff = (data) =>
	data?.map((professional) => ({
		id: professional.caregiverId,
		name: professional.name || '-',
		phone: professional.phone || '-',
		licenseType: formatLicenseType(professional.shiftTypes),
		specialty: formatSpecialty(professional.shiftTypes),
		shiftTypes: professional.shiftTypes,
		expandable: professional.shiftTypes.length > 1
	})) || [];

const specialtyCombosTemplate = (shiftType) => shiftType.specialtyShortName || '-';

const ShiftMedOnDemandStaffList = ({ managementCompany }) => {
	const [expandedRows, setExpandedRows] = useState(null);

	const { data, isLoading } = useGetOnDemandStaffByManagementCompanyQuery(managementCompany.id, {
		refetchOnMountOrArgChange: true
	});

	const professionals = useMemo(() => mapOnDemandStaff(data), [data]);
	const count = professionals.length;

	const allowExpansion = useCallback((rowData) => rowData.expandable, []);

	const cellClass = useCallback(() => ({ 'table-cell': true }), []);

	const rowClass = useCallback(
		(rowData) => ({
			'expanded-bundle-row': !!expandedRows && Object.prototype.hasOwnProperty.call(expandedRows, rowData.id)
		}),
		[expandedRows]
	);

	const actionsTemplate = useCallback(
		() => (
			<div className="actions-container">
				<button type="button" className="btn action-menu" disabled aria-label="View profile">
					<img src={personIcon} alt="View profile" />
				</button>
			</div>
		),
		[]
	);

	const rowExpansionTemplate = useCallback(
		(rowData) => (
			<div className="shiftmed-combos">
				<DataTable value={rowData.shiftTypes} size="small" tableStyle={{ tableLayout: 'fixed' }} onSort={noopSort}>
					<Column style={{ width: EXPANSION_LEADING_WIDTH }} />
					<Column
						field="licenseShortCode"
						header="License type"
						style={{ width: COLUMN_WIDTHS.licenseType }}
						sortable
					/>
					<Column
						header="Specialty"
						style={{ width: COLUMN_WIDTHS.specialty }}
						body={specialtyCombosTemplate}
						sortable
					/>
					<Column style={{ width: COLUMN_WIDTHS.actions }} />
				</DataTable>
			</div>
		),
		[]
	);

	return (
		<div className="professionals-list shiftmed-on-demand-staff-list">
			<div className="toolbar">
				<p className="total-count">
					{count} Professional{count === 1 ? '' : 's'}
				</p>
			</div>
			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={professionals}
						scrollable
						scrollHeight="flex"
						dataKey="id"
						tableStyle={{ minWidth: '60rem', tableLayout: 'fixed' }}
						size="small"
						stripedRows
						cellClassName={cellClass}
						rowClassName={rowClass}
						loading={isLoading}
						loadingIcon={<LoadingSpinner />}
						emptyMessage="No results found"
						expandedRows={expandedRows}
						onRowToggle={(event) => setExpandedRows(event.data)}
						rowExpansionTemplate={rowExpansionTemplate}
						onSort={noopSort}
					>
						<Column expander={allowExpansion} style={{ width: COLUMN_WIDTHS.expander }} />
						<Column field="name" header="Professional" style={{ width: COLUMN_WIDTHS.professional }} sortable />
						<Column field="phone" header="Phone" style={{ width: COLUMN_WIDTHS.phone }} sortable />
						<Column field="licenseType" header="License type" style={{ width: COLUMN_WIDTHS.licenseType }} sortable />
						<Column field="specialty" header="Specialty" style={{ width: COLUMN_WIDTHS.specialty }} sortable />
						<Column header="Actions" style={{ width: COLUMN_WIDTHS.actions }} body={actionsTemplate} />
					</DataTable>
				</div>
			</div>
		</div>
	);
};

export default memo(ShiftMedOnDemandStaffList);
